import { useEffect, useRef } from 'react';

/**
 * Esc closes the top-most layer.
 *
 * Every dialog, dropdown and picker registers itself here instead of adding its own
 * `document.addEventListener('keydown', …)`. One shared listener then calls only the LAST
 * registered handler, so Esc peels one layer at a time: a partner dropdown open inside the
 * New Project form closes the dropdown, and the next Esc closes the form. With per-component
 * listeners both fired at once and the form vanished under the user's hands.
 *
 * Registration order is mount order, which is also stacking order — that is why the effect
 * depends on `active` only. `onClose` is read through a ref, so a re-render of the parent does
 * not pop and re-push its entry above a child that opened after it.
 */
const stack = [];
let attached = false;

function onKeyDown(event) {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  // A native <select> handles Esc itself (it closes its own popup); intercepting it there would
  // shut the whole form while the user only meant to abandon the option list.
  const el = event.target;
  if (el && el.tagName === 'SELECT') return;
  event.preventDefault();
  top.close();
}

export default function useEscapeClose(onClose, active = true) {
  const ref = useRef(onClose);
  ref.current = onClose;

  useEffect(() => {
    if (!active) return undefined;
    const entry = { close: () => { if (typeof ref.current === 'function') ref.current(); } };
    stack.push(entry);
    if (!attached) {
      document.addEventListener('keydown', onKeyDown);
      attached = true;
    }
    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      if (!stack.length && attached) {
        document.removeEventListener('keydown', onKeyDown);
        attached = false;
      }
    };
  }, [active]);
}
